import notify from '@helpers/toastify-helper';
import { fetchEntityList, getReadableOrderIdList } from '@services/order.service';
import { getSupplierNames } from '@services/supplier.service';
import { useEffect, useState } from 'react';
import css from './index.module.scss';
import SelectLabel from '@components/common/select-label';
import { AutoComplete, InputDatePicker, SearchBar } from '@components/common';
import { Button, CheckboxLabel, IconNode, ToolTip, Typography } from '@components/base';
import Images from '@assets/images';

export interface FinanceFilterDrawerProps {
  onClose: () => void;
  onApply: (data: any) => void;
  value: any;
}

interface IOrderList {
  order_id: string;
  readable_order_id: string;
}

interface ISupplierIdName {
  supplier_id: string;
  supplier_name: string;
}

interface IEntity {
  entity_id: string;
  name: string;
}

export interface FinanceFilterDrawerState {
  isLoading: boolean;
  entityList: IEntity[];
  supplierList: ISupplierIdName[];
  orderIdList: IOrderList[];
  selectedEntity: IEntity | null;
  selectedSupplier: ISupplierIdName | null;
  selectedOrderId: IOrderList | null;
  selectedStartDate: Date | null;
  selectedEndDate: Date | null;
  excludeZeroOutstanding: boolean;
}

const FinanceFilterDrawer = (props: any) => {
  const { onClose, value } = props;

  const [drawerState, setDrawerState] = useState<FinanceFilterDrawerState>({
    isLoading: false,
    entityList: [],
    supplierList: [],
    orderIdList: [],
    selectedEntity: null,
    selectedSupplier: null,
    selectedOrderId: null,
    selectedStartDate: null,
    selectedEndDate: null,
    excludeZeroOutstanding: false
  });

  const {
    isLoading,
    entityList,
    supplierList,
    orderIdList,
    selectedEntity,
    selectedSupplier,
    selectedOrderId,
    selectedStartDate,
    selectedEndDate,
    excludeZeroOutstanding
  } = drawerState;

  const fetchSupplierNames = async () => {
    setDrawerState((prev: FinanceFilterDrawerState) => ({ ...prev, isLoading: true }));
    const response = await getSupplierNames();
    if (response?.success) {
      setDrawerState((prevState: FinanceFilterDrawerState) => ({
        ...prevState,
        supplierList: response?.data ?? [],
        isLoading: false
      }));
    } else {
      notify({
        severity: 'error',
        message: response?.message
      });
      setDrawerState((prev: FinanceFilterDrawerState) => ({ ...prev, isLoading: false }));
    }
  };

  const fetchOrderList = async (event?: any, param?: string) => {
    setDrawerState((prev: FinanceFilterDrawerState) => ({ ...prev, isLoading: true }));
    const response = await getReadableOrderIdList(param);
    if (response?.success) {
      setDrawerState((prevState: FinanceFilterDrawerState) => ({
        ...prevState,
        orderIdList: response?.data?.results ?? [],
        isLoading: false
      }));
    } else {
      notify({
        severity: 'error',
        message: response?.message
      });
      setDrawerState((prev: FinanceFilterDrawerState) => ({ ...prev, isLoading: false }));
    }
  };

  const getEntityList = async () => {
    const response = await fetchEntityList();
    if (response?.success) {
      setDrawerState((prevState: FinanceFilterDrawerState) => ({
        ...prevState,
        entityList: response?.data?.results ?? []
      }));
    } else {
      notify({
        severity: 'error',
        message: response?.message
      });
    }
  };

  const onReset = () => {
    setDrawerState((prevState: FinanceFilterDrawerState) => ({
      ...prevState,
      selectedEntity: null,
      selectedSupplier: null,
      selectedOrderId: null,
      selectedStartDate: null,
      selectedEndDate: null,
      excludeZeroOutstanding: false
    }));
  };

  useEffect(() => {
    fetchSupplierNames();
    fetchOrderList();
    getEntityList();
  }, []);

  useEffect(() => {
    value &&
      setDrawerState(() => {
        return {
          ...value,
          isLoading: false,
          selectedOrderId: value?.selectedOrderId
        };
      });
  }, [value]);

  return (
    <div className={css.mainWrapper}>
      <div className={css.headerWrapper}>
        <Typography variant="h3">Filter</Typography>
        <div className={css.actionWrapper}>
          <Button
            onClick={() => {
              onReset();
            }}>
            Reset Form
          </Button>
          <Button
            onClick={() => {
              props.onApply(drawerState);
            }}
            variant="outlined-secondary">
            Apply Filters
          </Button>
          <IconNode src={Images.close} alt="filter icon" onClick={onClose} />
        </div>
      </div>
      <div className={css.filterWrapper}>
        <SelectLabel
          rootClassName={css.selectLabel}
          label="Entity"
          placeholder="Select Entity"
          isSearchable
          value={selectedEntity}
          options={entityList}
          isClearable
          getOptionLabel={(options: any) => options?.name}
          getOptionValue={(options: any) => options?.entity_id_id}
          onChange={(value: any) => {
            setDrawerState((prevState: FinanceFilterDrawerState) => ({
              ...prevState,
              selectedEntity: value
            }));
          }}
        />

        <SelectLabel
          rootClassName={css.selectLabel}
          label="Supplier"
          placeholder="Select Supplier"
          isSearchable
          value={selectedSupplier}
          options={supplierList}
          isClearable
          getOptionLabel={(options: any) => options?.supplier_name}
          getOptionValue={(options: any) => options?.supplier_id}
          onChange={(value: any) => {
            setDrawerState((prevState: FinanceFilterDrawerState) => ({
              ...prevState,
              selectedSupplier: value
            }));
          }}
        />
        <div className={css.autoCompleteWrapper}>
          {!props?.order_id && (
            <AutoComplete
              label="Order ID"
              options={orderIdList}
              value={selectedOrderId}
              onInputChange={(event: any, param: string) => fetchOrderList(event, param)}
              onInputSelection={(event: any, param: any) => {
                setDrawerState((prevState: FinanceFilterDrawerState) => ({
                  ...prevState,
                  selectedOrderId: param
                }));
              }}
              keyOption="readable_order_id"
              getOptionValue={(option: any) => option.readable_order_id}
              placeholder="Search Order"
              width="100%"
            />
          )}
        </div>

        <InputDatePicker
          label="Start Date"
          value={selectedStartDate}
          onSelect={(day: Date | undefined) =>
            setDrawerState((prevState: FinanceFilterDrawerState) => ({
              ...prevState,
              selectedStartDate: day ?? null
            }))
          }
          rootClassName={css.fieldSpacing}
        />

        <InputDatePicker
          label="End Date"
          value={selectedEndDate}
          onSelect={(day: Date | undefined) =>
            setDrawerState((prevState: FinanceFilterDrawerState) => ({
              ...prevState,
              selectedEndDate: day ?? null
            }))
          }
          rootClassName={css.fieldSpacing}
        />

        <CheckboxLabel
          label="Exclude invoices with zero outstanding payment"
          checked={excludeZeroOutstanding}
          onChange={(e) => {
            setDrawerState((prevState: FinanceFilterDrawerState) => ({
              ...prevState,
              excludeZeroOutstanding: e.target.checked
            }));
          }}
        />
      </div>
    </div>
  );
};

export default FinanceFilterDrawer;
